import React, { useRef, useCallback, useEffect } from "react";
import { Box } from "@chakra-ui/react";

interface JinjaHighlightedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isReadOnly?: boolean;
}

const JinjaHighlightedTextarea: React.FC<JinjaHighlightedTextareaProps> = ({
  value,
  onChange,
  placeholder,
  isReadOnly = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Sync scroll from textarea to highlight layer (single source of truth)
  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleScroll = () => syncScroll();

  // Re-sync when returning to the tab or when textarea gains focus so caret aligns with highlighted text
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") syncScroll();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [syncScroll]);

  const handleFocus = () => syncScroll();

  // Highlight Jinja syntax
  const highlightJinja = (text: string) => {
    if (!text) return "";

    // Escape HTML
    text = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Use color only (no font-weight/italic) so character widths match the textarea and cursor stays aligned
    // Highlight Jinja variables {{ }}
    text = text.replace(/(\{\{.*?\}\})/g, "<span style=\"color:rgb(158, 18, 8);\">$1</span>");

    // Highlight Jinja statements {% %}
    text = text.replace(/(\{%.*?%\})/g, "<span style=\"color:rgb(28, 75, 177);\">$1</span>");

    // Highlight Jinja comments {# #}
    text = text.replace(/(\{#.*?#\})/g, "<span style=\"color:rgb(19, 128, 59);\">$1</span>");

    return text;
  };

  return (
    <Box position="relative" flex="1" height="100%">
      {/* Highlighted background layer */}
      <Box
        ref={highlightRef}
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        overflow="auto"
        pointerEvents="none"
        padding="8px 12px"
        fontSize="sm"
        fontFamily="'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
        lineHeight="1.5"
        whiteSpace="pre-wrap"
        color="gray.800"
        textAlign="left"
        dangerouslySetInnerHTML={{ __html: highlightJinja(value) || `<span style="color: #a0aec0;">${placeholder || ""}</span>` }}
        css={{
          wordWrap: "break-word",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
        }}
        bg={isReadOnly ? "gray.50" : "transparent"}
      />

      {/* Textarea overlay */}
      <Box
        as="textarea"
        ref={textareaRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        onScroll={handleScroll}
        onFocus={handleFocus}
        readOnly={isReadOnly}
        placeholder={placeholder}
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        padding="8px 12px"
        fontSize="sm"
        fontFamily="'Monaco', 'Menlo', 'Ubuntu Mono', monospace"
        lineHeight="1.5"
        resize="none"
        border="1px solid"
        borderColor="gray.300"
        borderRadius="md"
        background="transparent"
        color="transparent"
        outline="none"
        textAlign="left"
        _hover={{ borderColor: "blue.400" }}
        _focus={{ borderColor: "blue.500", boxShadow: "none" }}
        cursor={isReadOnly ? "not-allowed" : "text"}
        css={{
          caretColor: "black",
          "&::placeholder": {
            color: "transparent",
          },
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
        }}
      />
    </Box>
  );
};

export default JinjaHighlightedTextarea;

